import React, { useEffect, useState, useRef } from 'react';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from "@mui/material/Tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Box,
  FormControl,
  Select,
  MenuItem,
  Pagination,
  Typography,
  Card,
  CardContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { format } from 'date-fns';
import { useDispatch, useSelector } from 'react-redux';
import { fetchPolicyAllActivity } from '../../../redux/actions/activityAction';
import { getActivityListDataAction } from '../../../redux/actions/customerSearchAction';

const StyledTableContainer = styled(TableContainer)(({ theme }) => ({
  margin: '16px auto',
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
  [theme.breakpoints.down('md')]: {
    margin: '8px auto',
  }
}));

const StyledCard = styled(Card)(({ theme }) => ({
  margin: '8px 0',
  boxShadow: theme.shadows[1],
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.divider}`,
}));

// Custom hook to detect text overflow
const useIsOverflow = (ref, text) => {
  const [isOverflowed, setIsOverflowed] = useState(false);

  useEffect(() => {
    if (ref.current && text) {
      const element = ref.current;
      setIsOverflowed(
        element.scrollWidth > element.clientWidth ||
        element.scrollHeight > element.clientHeight
      );
    }
  }, [ref, text]);

  return isOverflowed;
};

// Component for conditionally showing tooltip only when text is truncated
const OverflowTooltip = ({ text, children, ...tooltipProps }) => {
  const ref = useRef(null);
  const isOverflowed = useIsOverflow(ref, text);

  return (
    <Tooltip 
      title={isOverflowed ? text : ''} 
      disableHoverListener={!isOverflowed}
      {...tooltipProps}
    >
      {React.cloneElement(children, { ref })}
    </Tooltip>
  );
};

const Activity = ({ customerId, selectedOption, selectedPolicyId }) => {
  const dispatch = useDispatch();
  const activityData = useSelector((state) => state.allActivity.all_activity);
  const activityList = activityData.content || [];
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const formatTimestamp = (timestamp) => {
    return format(new Date(timestamp), isMobile ? 'MMM dd, yyyy' : 'MMM dd, yyyy HH:mm:ss');
  };

  useEffect(() => {
    if (selectedOption === "All" && customerId) {
      dispatch(getActivityListDataAction(customerId, page, rowsPerPage));
    } else {
      dispatch(fetchPolicyAllActivity(selectedPolicyId, page, rowsPerPage));
    }
  }, [dispatch, page, rowsPerPage, customerId, selectedPolicyId]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getChipColor = (actionType) => {
    switch (actionType.toLowerCase()) {
      case "get":
        return "secondary";
      case "delete":
        return "error";
      case "create":
        return "success";
      case "update":
        return "info";
      default:
        return "default";
    }
  };

  // Mobile Card View
  const MobileActivityCard = ({ log }) => {
    const descriptionRef = useRef(null);
    const isDescriptionOverflowed = useIsOverflow(descriptionRef, log.description);

    return (
      <StyledCard>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={log.entityType}
                color="success"
                size="small"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
              <Chip
                label={log.actionType}
                color={getChipColor(log.actionType)}
                size="small"
                sx={{ fontSize: '0.7rem', height: '20px' }}
              />
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1, flexShrink: 0 }}>
              {formatTimestamp(log.timestamp)}
            </Typography>
          </Box>

          {log.policyNumber && (
            <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
              Policy: {log.policyNumber}
            </Typography>
          )}

          <Box sx={{ mb: 1 }}>
            <OverflowTooltip
              text={log.description}
              arrow
              placement="top"
              componentsProps={{
                tooltip: {
                  sx: {
                    backgroundColor: "white",
                    color: "black",
                    boxShadow: 1,
                    border: "1px solid #ccc",
                    fontSize: "12px",
                    fontWeight: "bold",
                    maxWidth: '300px'
                  },
                },
                arrow: {
                  sx: { color: "white" },
                },
              }}
            >
              <Typography 
                ref={descriptionRef}
                variant="body2" 
                color="text.secondary"
                sx={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  wordBreak: 'break-word'
                }}
              >
                {log.description}
              </Typography>
            </OverflowTooltip>
          </Box>

          <Typography variant="caption" color="text.secondary">
            By: {log.userFullName}
          </Typography>
        </CardContent>
      </StyledCard>
    );
  };

  return (
    <Box sx={{
      mt: 2,
      mx: { xs: 1, sm: 2 },
      mb: 2
    }}>
      {isMobile ? (
        // Mobile Card Layout
        <Box>
          <Typography variant="h6" sx={{ mb: 2, px: 1 }}>
            Activity Log
          </Typography>
          {activityList.length > 0 ? (
            activityList.map((log) => (
              <MobileActivityCard key={log.id} log={log} />
            ))
          ) : (
            <Card>
              <CardContent>
                <Typography variant="body2" color="text.secondary" textAlign="center">
                  No activity data available
                </Typography>
              </CardContent>
            </Card>
          )}
        </Box>
      ) : (
        // Desktop Table Layout
        <StyledTableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell className="styled-table-heading">Activity Type</TableCell>
                <TableCell className="styled-table-heading">Policy Number</TableCell>
                <TableCell className="styled-table-heading">Action</TableCell>
                <TableCell className="styled-table-heading">Log Description</TableCell>
                <TableCell className="styled-table-heading">User Name</TableCell>
                <TableCell className="styled-table-heading">Log Date</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {activityList.map((log) => (
                <TableRow key={log.id}>
                  <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>
                    <Chip sx={{fontFamily: `"Poppins", sans-serif`,}} label={log.entityType} color="success" size="small" />
                  </TableCell>
                  <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>{log.policyNumber || '-'}</TableCell>
                  <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>
                    <Chip
                    sx={{fontFamily: `"Poppins", sans-serif`,}}
                      label={log.actionType}
                      color={getChipColor(log.actionType)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell sx={{
                    maxWidth: '200px',
                    wordBreak: 'break-word'
                  }}>
                    <OverflowTooltip
                      text={log.description}
                      arrow
                      placement="top"
                      componentsProps={{
                        tooltip: {
                          sx: {
                            fontFamily: `"Poppins", sans-serif`,
                            backgroundColor: "white",
                            color: "black",
                            boxShadow: 1,
                            border: "1px solid #ccc",
                            fontSize: "14px",
                            fontWeight: "bold",
                            maxWidth: '400px',
                            wordBreak: 'break-word'
                          },
                        },
                        arrow: {
                          sx: { color: "white" },
                        },
                      }}
                    >
                      <Typography 
                        variant="body2"
                        sx={{
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block'
                        }}
                      >
                        {log.description}
                      </Typography>
                    </OverflowTooltip>
                  </TableCell>
                  <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>{log.userFullName}</TableCell>
                  <TableCell sx={{fontFamily: `"Poppins", sans-serif`,}}>{formatTimestamp(log.timestamp)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </StyledTableContainer>
      )}

      {/* Responsive Pagination */}
      {activityData?.totalElements > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: { xs: 'center', sm: 'flex-end' },
            alignItems: 'center',
            gap: { xs: 2, sm: 1 },
            mt: 2,
            px: { xs: 1, sm: 2 },
          }}
        >
          {/* Rows per page dropdown */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            order: { xs: 2, sm: 1 }
          }}>
            <Typography variant="body2" sx={{ mr: 1, fontSize: { xs: '12px', sm: '14px' } }}>
              Rows per page:
            </Typography>
            <FormControl size="small">
              <Select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                displayEmpty
                sx={{
                  fontSize: { xs: '12px', sm: '14px' },
                  minWidth: '60px'
                }}
              >
                {[5, 10, 15, 25, 50, 100].map((option) => (
                  <MenuItem key={option} value={option} sx={{ fontSize: { xs: '12px', sm: '14px' } }}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>

          {/* Pagination buttons */}
          <Pagination
            count={Math.ceil((activityData?.totalElements || 0) / rowsPerPage)}
            page={page + 1}
            onChange={(event, newPage) => handleChangePage(event, newPage - 1)}
            size={isSmallMobile ? "small" : "medium"}
            sx={{
              order: { xs: 1, sm: 2 },
              '& .MuiPaginationItem-root': {
                fontSize: { xs: '12px', sm: '14px' }
              }
            }}
          />
        </Box>
      )}
    </Box>
  );
};

export default Activity;